/**
 * @module features/devices/components/DeviceRegisterModal
 * @description Responsive Drawer/Sheet for onboarding new hardware devices.
 * On mobile, opens as an edge-to-edge bottom sheet with a grab handle indicator.
 * On desktop/tablet, slides out from the right (480px).
 * The type selector drives which type-specific field group renders.
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { AlertCircle, Loader2, Wand2 } from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import { useCreateDevice } from "../api/useDeviceMutations";
import { DeviceCreateFormValues, DeviceType } from "../types";
import { generateRegisteredDeviceId } from "../utils/idGenerator";
import { isValidMac, normalizeMac } from "../utils/macFormatter";
import { deviceTypeLabel } from "../utils/format";
import { DisplayModuleFields } from "./DisplayModuleFields";

const TYPE_FIELD_GROUPS: Record<DeviceType, typeof DisplayModuleFields> = {
  display_module: DisplayModuleFields,
};
const SUPPORTED_TYPES = Object.keys(TYPE_FIELD_GROUPS) as DeviceType[];

interface DeviceRegisterModalProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULTS: DeviceCreateFormValues = {
  device_name: "",
  mac_address: "",
  registered_device_id: "",
  device_type: "display_module",
  os_name: "",
  os_version: "",
  os_hash: "",
};

export function DeviceRegisterModal({ open, onClose }: DeviceRegisterModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DeviceCreateFormValues>({ defaultValues: DEFAULTS });
  const createMutation = useCreateDevice();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [requestId, setRequestId] = React.useState<string | null>(null);

  const deviceType = watch("device_type");
  const TypeFields = TYPE_FIELD_GROUPS[deviceType];

  const handleClose = () => {
    reset(DEFAULTS);
    setErrorMessage(null);
    setRequestId(null);
    onClose();
  };

  const onSubmit = async (values: DeviceCreateFormValues) => {
    setErrorMessage(null);
    setRequestId(null);
    try {
      const cleanMac = values.mac_address?.trim()
        ? normalizeMac(values.mac_address)
        : undefined;
      await createMutation.mutateAsync({
        ...values,
        device_name: values.device_name.trim(),
        mac_address: cleanMac,
        registered_device_id: values.registered_device_id?.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      // Input is preserved so the admin can fix and retry.
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
        setRequestId(err.requestId);
      } else {
        setErrorMessage("Failed to register the device. Please try again.");
      }
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Register Device"
      description="Add a hardware unit to the fleet. It starts as Provisioned."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errorMessage && (
          <div
            role="alert"
            className="flex items-start gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p>{errorMessage}</p>
              {requestId && (
                <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                  Request ID: {requestId}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <label id="device_type_label" className="text-xs font-medium text-foreground block">
            Device type
          </label>
          <Select
            value={deviceType}
            onValueChange={(val: DeviceType) =>
              setValue("device_type", val, { shouldValidate: true })
            }
          >
            <SelectTrigger aria-labelledby="device_type_label" className="h-10 bg-card rounded-xl">
              <SelectValue placeholder="Select device type" />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {deviceTypeLabel(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label htmlFor="device_name" className="text-xs font-medium text-foreground block">
            Device name <span className="text-destructive">*</span>
          </label>
          <Input
            id="device_name"
            placeholder="Lobby Display 1"
            aria-invalid={Boolean(errors.device_name)}
            {...register("device_name", {
              required: "Device name is required.",
              maxLength: { value: 100, message: "Device name cannot exceed 100 characters." },
              validate: (v) => v.trim().length > 0 || "Device name is required.",
            })}
            className="h-10 text-sm bg-card"
          />
          {errors.device_name && (
            <p role="alert" className="text-[11px] text-destructive">
              {errors.device_name.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="mac_address" className="text-xs font-medium text-foreground block">
            MAC address{" "}
            {deviceType === "display_module" ? (
              <span className="text-destructive">*</span>
            ) : (
              <span className="text-muted-foreground font-normal">(optional)</span>
            )}
          </label>
          <Input
            id="mac_address"
            placeholder={
              deviceType === "display_module"
                ? "AA:BB:CC:11:22:33"
                : "AA:BB:CC:11:22:33 (optional)"
            }
            autoComplete="off"
            spellCheck={false}
            aria-invalid={Boolean(errors.mac_address)}
            {...register("mac_address", {
              required:
                deviceType === "display_module" ? "MAC address is required." : false,
              validate: (v) => {
                if (!v || !v.trim()) {
                  return deviceType === "display_module"
                    ? "MAC address is required."
                    : true;
                }
                return (
                  isValidMac(v) || "Invalid MAC address (e.g. AA:BB:CC:11:22:33)."
                );
              },
            })}
            className="h-10 text-sm font-mono bg-card"
          />
          {errors.mac_address && (
            <p role="alert" className="text-[11px] text-destructive">
              {errors.mac_address.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="registered_device_id"
            className="text-xs font-medium text-foreground block"
          >
            Registered device ID
          </label>
          <div className="flex gap-2">
            <Input
              id="registered_device_id"
              placeholder="Serial / barcode, or auto-generate"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(errors.registered_device_id)}
              {...register("registered_device_id", {
                maxLength: { value: 100, message: "Cannot exceed 100 characters." },
              })}
              className="h-10 flex-1 text-sm font-mono bg-card"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setValue("registered_device_id", generateRegisteredDeviceId(), {
                  shouldValidate: true,
                })
              }
              className="h-10 min-w-[44px] gap-1.5 text-xs"
              title="Generate a DEV- registration code"
            >
              <Wand2 className="h-4 w-4" />
              <span className="hidden sm:inline">Auto-Generate</span>
              <span className="sr-only sm:hidden">Auto-Generate</span>
            </Button>
          </div>
          {errors.registered_device_id && (
            <p role="alert" className="text-[11px] text-destructive">
              {errors.registered_device_id.message}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Leave blank and the system will generate one for you.
          </p>
        </div>

        <TypeFields register={register} errors={errors} />

        <div className="pt-3 flex items-center justify-end gap-2 border-t border-border mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="h-9 px-4 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="h-9 px-4 text-xs min-w-[130px] shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registering...
              </>
            ) : errorMessage ? (
              "Retry"
            ) : (
              "Register Device"
            )}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
